import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import portalApi, { resolveFileUrl } from '../../../../lib/portalApi';
import {
  FolderLock,
  Upload,
  FileText,
  Download,
  CheckCircle,
  Clock,
  AlertTriangle,
  History,
  Shield,
  Eye,
  X,
  FileCheck,
  AlertCircle,
  ChevronRight,
  Filter,
  RefreshCw,
  Files,
  CheckCircle2,
  UploadCloud,
} from 'lucide-react';

export default function StartupVaultPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter') || 'all';

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, approved: 0, in_review: 0, rejected: 0, not_uploaded: 0 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialFilter); // 'all', 'approved', 'in_review', 'rejected', 'not_uploaded'
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Modals state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [targetItem, setTargetItem] = useState(null); // Document being uploaded or resubmitted
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [versionHistoryData, setVersionHistoryData] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadHeading, setUploadHeading] = useState('');
  const [uploadCategory, setUploadCategory] = useState('Legal & Compliance');
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fileInputRef = useRef(null);

  const loadVaultData = async () => {
    try {
      setLoading(true);
      const res = await portalApi.getStartupPortalVault();
      setItems(res?.items || []);
      setCounts(res?.counts || { all: 0, approved: 0, in_review: 0, rejected: 0, not_uploaded: 0 });
      setCategories(res?.categories || []);
    } catch (err) {
      console.error('Failed to load vault items:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVaultData();
  }, []);

  // Sync tab with URL search parameter if changed
  useEffect(() => {
    const f = searchParams.get('filter');
    if (f && ['all', 'approved', 'in_review', 'rejected', 'not_uploaded'].includes(f)) {
      setActiveTab(f);
    }
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'all' ? {} : { filter: tab });
  };

  // Filtered documents
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      // Tab filter
      if (activeTab === 'approved' && !['Approved', 'Signed', 'Read'].includes(item.status)) return false;
      if (activeTab === 'in_review' && !['In Review', 'Uploaded', 'Requested'].includes(item.status)) return false;
      if (activeTab === 'rejected' && item.status !== 'Rejected') return false;
      if (activeTab === 'not_uploaded' && item.status !== 'Not Uploaded') return false;

      // Category filter
      if (selectedCategory !== 'All' && item.category !== selectedCategory) return false;

      return true;
    });
  }, [items, activeTab, selectedCategory]);

  // Open upload modal for a specific requirement or brand new document
  const openUploadModal = (item = null) => {
    setTargetItem(item);
    setUploadFile(null);
    setUploadError('');
    setUploadProgress(0);
    if (item) {
      setUploadHeading(item.heading);
      setUploadCategory(item.category || 'Legal & Compliance');
    } else {
      setUploadHeading('');
      setUploadCategory('General');
    }
    setUploadModalOpen(true);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSelectFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSelectFile(e.target.files[0]);
    }
  };

  const validateAndSelectFile = (file) => {
    setUploadError('');
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Invalid file type. Only PDF, JPG, or PNG files are accepted.');
      return;
    }
    // 10MB limit
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size exceeds the 10 MB limit.');
      return;
    }
    setUploadFile(file);
    if (!uploadHeading) {
      setUploadHeading(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      setUploadError('Please select a file to upload.');
      return;
    }
    if (!uploadHeading.trim()) {
      setUploadError('Please provide a document title.');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(30);

      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('heading', uploadHeading.trim());
      formData.append('category', uploadCategory);

      if (targetItem && targetItem.id) {
        formData.append('documentId', targetItem.id);
        if (targetItem.isRequired !== undefined) {
          formData.append('isRequired', String(targetItem.isRequired));
        }
      }

      setUploadProgress(70);
      await portalApi.uploadStartupPortalDoc(formData);
      setUploadProgress(100);

      // Close modal and refresh data
      setUploadModalOpen(false);
      await loadVaultData();
    } catch (err) {
      setUploadError(err.message || 'Failed to upload document.');
    } finally {
      setUploading(false);
    }
  };

  // Version history viewer
  const viewVersions = async (item) => {
    if (!item.id || item.id.startsWith('req_')) return;
    try {
      setLoadingVersions(true);
      setVersionModalOpen(true);
      const res = await portalApi.getStartupPortalDocVersions(item.id);
      setVersionHistoryData(res);
    } catch (err) {
      console.error('Failed to load version history:', err);
    } finally {
      setLoadingVersions(false);
    }
  };

  const rejectedCount = counts.rejected || 0;

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold text-white tracking-tight">Documents Vault</h1>
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-[#D42B92]/10 text-[#FF8EC5] border border-[#D42B92]/20 flex items-center gap-1.5">
              <FolderLock className="w-3 h-3 text-[#FF8EC5]" />
              Zero-Trust Isolated
            </span>
          </div>
          <p className="text-sm text-white/60 mt-1">
            Official repository for accelerator agreements, corporate compliance, financial models, and pitch materials.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => loadVaultData()}
            className="p-2 rounded-xl border border-[#2C2C34] bg-[#131318] text-[#8E8E99] hover:text-[#F2F2F4] hover:border-[#D42B92]/40 transition-colors"
            title="Refresh Vault"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => openUploadModal(null)}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#D42B92] to-[#b02279] hover:from-[#e0349f] hover:to-[#be2783] text-white font-medium text-sm flex items-center gap-2 shadow-lg shadow-[#D42B92]/20 transition-all"
          >
            <Upload className="w-4 h-4" />
            Upload Document
          </button>
        </div>
      </div>

      {/* Critical Rejection Alert Banner */}
      {rejectedCount > 0 && (
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 flex items-start gap-3.5 animate-pulse">
          <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-rose-300">Action Required: Document Revision Requested</h4>
            <p className="text-xs text-rose-200/80 mt-0.5">
              {rejectedCount} document{rejectedCount > 1 ? 's were' : ' was'} reviewed and returned with feedback.
              Please inspect reviewer comments below and upload revised versions.
            </p>
          </div>
          <button
            onClick={() => handleTabChange('rejected')}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-500/30 transition-colors"
          >
            View Rejected ({rejectedCount})
          </button>
        </div>
      )}

      {/* ─── 5 Status Summary Cards (Interactive Filtering) ─── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
        {/* Card 1: All */}
        <div
          onClick={() => handleTabChange('all')}
          className={`cursor-pointer rounded-2xl p-4 sm:p-5 border transition-all duration-200 group ${
            activeTab === 'all'
              ? 'bg-[#1e131d] border-[#D42B92] shadow-lg shadow-[#D42B92]/15 ring-1 ring-[#D42B92]/40'
              : 'bg-[#131318] border-[#2C2C34] hover:border-white/20 hover:bg-[#181820]'
          }`}
        >
          <div className="flex items-center justify-between text-[#8E8E99] mb-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider">All Documents</span>
            <div
              className={`p-2 rounded-xl transition-colors ${
                activeTab === 'all'
                  ? 'bg-[#D42B92]/20 text-[#FF8EC5]'
                  : 'bg-white/[0.04] text-[#8E8E99] group-hover:text-white'
              }`}
            >
              <Files className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-[#F2F2F4]">
            {loading ? '…' : (counts.all || 0)}
          </div>
          <div className="text-xs text-[#8E8E99] mt-1.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D42B92]" />
            <span>Total vault items</span>
          </div>
        </div>

        {/* Card 2: Approved */}
        <div
          onClick={() => handleTabChange('approved')}
          className={`cursor-pointer rounded-2xl p-4 sm:p-5 border transition-all duration-200 group ${
            activeTab === 'approved'
              ? 'bg-[#0f1d18] border-emerald-500 shadow-lg shadow-emerald-500/15 ring-1 ring-emerald-500/40'
              : 'bg-[#131318] border-[#2C2C34] hover:border-emerald-500/40 hover:bg-[#181820]'
          }`}
        >
          <div className="flex items-center justify-between text-[#8E8E99] mb-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Approved</span>
            <div
              className={`p-2 rounded-xl transition-colors ${
                activeTab === 'approved'
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'bg-white/[0.04] text-emerald-400/80 group-hover:text-emerald-300'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-[#F2F2F4]">
            {loading ? '…' : (counts.approved || 0)}
          </div>
          <div className="text-xs text-[#8E8E99] mt-1.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <span>Verified & cleared</span>
          </div>
        </div>

        {/* Card 3: In Review */}
        <div
          onClick={() => handleTabChange('in_review')}
          className={`cursor-pointer rounded-2xl p-4 sm:p-5 border transition-all duration-200 group ${
            activeTab === 'in_review'
              ? 'bg-[#1f1b13] border-amber-500 shadow-lg shadow-amber-500/15 ring-1 ring-amber-500/40'
              : 'bg-[#131318] border-[#2C2C34] hover:border-amber-500/40 hover:bg-[#181820]'
          }`}
        >
          <div className="flex items-center justify-between text-[#8E8E99] mb-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider">In Review</span>
            <div
              className={`p-2 rounded-xl transition-colors ${
                activeTab === 'in_review'
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-white/[0.04] text-amber-400/80 group-hover:text-amber-300'
              }`}
            >
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-[#F2F2F4]">
            {loading ? '…' : (counts.in_review || 0)}
          </div>
          <div className="text-xs text-[#8E8E99] mt-1.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            <span>Audit in progress</span>
          </div>
        </div>

        {/* Card 4: Rejected */}
        <div
          onClick={() => handleTabChange('rejected')}
          className={`cursor-pointer rounded-2xl p-4 sm:p-5 border transition-all duration-200 group ${
            activeTab === 'rejected'
              ? 'bg-[#1f1215] border-rose-500 shadow-lg shadow-rose-500/15 ring-1 ring-rose-500/40'
              : 'bg-[#131318] border-[#2C2C34] hover:border-rose-500/40 hover:bg-[#181820]'
          }`}
        >
          <div className="flex items-center justify-between text-[#8E8E99] mb-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Rejected</span>
            <div
              className={`p-2 rounded-xl transition-colors ${
                activeTab === 'rejected'
                  ? 'bg-rose-500/20 text-rose-400'
                  : 'bg-white/[0.04] text-rose-400/80 group-hover:text-rose-300'
              }`}
            >
              <AlertTriangle className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-[#F2F2F4]">
            {loading ? '…' : (counts.rejected || 0)}
          </div>
          <div className="text-xs text-[#8E8E99] mt-1.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
            <span>Revision required</span>
          </div>
        </div>

        {/* Card 5: Missing */}
        <div
          onClick={() => handleTabChange('not_uploaded')}
          className={`cursor-pointer rounded-2xl p-4 sm:p-5 border transition-all duration-200 group ${
            activeTab === 'not_uploaded'
              ? 'bg-[#141720] border-sky-500 shadow-lg shadow-sky-500/15 ring-1 ring-sky-500/40'
              : 'bg-[#131318] border-[#2C2C34] hover:border-sky-500/40 hover:bg-[#181820]'
          }`}
        >
          <div className="flex items-center justify-between text-[#8E8E99] mb-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Missing</span>
            <div
              className={`p-2 rounded-xl transition-colors ${
                activeTab === 'not_uploaded'
                  ? 'bg-sky-500/20 text-sky-400'
                  : 'bg-white/[0.04] text-sky-400/80 group-hover:text-sky-300'
              }`}
            >
              <UploadCloud className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-[#F2F2F4]">
            {loading ? '…' : (counts.not_uploaded || 0)}
          </div>
          <div className="text-xs text-[#8E8E99] mt-1.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
            <span>Action pending</span>
          </div>
        </div>
      </div>

      {/* Category Pills & Quick Filter Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-[#2C2C34] pb-4 pt-1">
        <div className="flex items-center gap-2 text-xs text-[#8E8E99]">
          <span>Filtering by:</span>
          <span className="px-2 py-0.5 rounded-md bg-[#2C2C34]/60 text-[#F2F2F4] font-medium capitalize">
            {activeTab.replace('_', ' ')} ({activeTab === 'all' ? counts.all : counts[activeTab] || 0})
          </span>
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto">
          <Filter className="w-3.5 h-3.5 text-white/40 mr-1 shrink-0" />
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-xl text-xs whitespace-nowrap transition-all ${
                selectedCategory === cat
                  ? 'bg-[#321129] text-[#FF8EC5] font-semibold border border-[#D42B92]/40 shadow-sm'
                  : 'text-[#8E8E99] hover:text-[#F2F2F4] bg-[#131318] border border-[#2C2C34]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Documents List / Checklist Grid */}
      <div className="rounded-2xl border border-[#2C2C34] bg-[#131318] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#8E8E99] text-sm flex items-center justify-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin text-[#FF8EC5]" />
            Loading vault documents...
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-12 text-center text-[#8E8E99] text-sm">
            No documents matching the active filter.
          </div>
        ) : (
          <div className="divide-y divide-[#2C2C34]/60">
            {filteredItems.map((doc) => {
              const isApproved = ['Approved', 'Signed', 'Read'].includes(doc.status);
              const isInReview = ['In Review', 'Uploaded'].includes(doc.status);
              const isRejected = doc.status === 'Rejected';
              const isNotUploaded = doc.status === 'Not Uploaded' || doc.status === 'Requested';

              return (
                <div
                  key={doc.id}
                  className={`p-4 transition-colors hover:bg-white/[0.02] ${
                    isRejected ? 'bg-rose-500/[0.04]' : ''
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Title & Metadata */}
                    <div className="flex items-start gap-3.5 flex-1 min-w-0">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
                          isApproved
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                            : isRejected
                            ? 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                            : isInReview
                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                            : 'bg-[#08080A] text-[#8E8E99] border-[#2C2C34]'
                        }`}
                      >
                        {isApproved ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : isRejected ? (
                          <AlertTriangle className="w-5 h-5" />
                        ) : isInReview ? (
                          <Clock className="w-5 h-5" />
                        ) : (
                          <FileText className="w-5 h-5" />
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-semibold text-white tracking-tight truncate">
                            {doc.heading}
                          </h3>
                          {doc.isRequired && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500/10 text-red-400 border border-red-500/20">
                              Required
                            </span>
                          )}
                          {doc.isConfidential && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#08080A] text-[#8E8E99] border border-[#2C2C34] flex items-center gap-1">
                              <Shield className="w-2.5 h-2.5 text-[#8E8E99]" />
                              Confidential
                            </span>
                          )}
                          {doc.version > 0 && (
                            <button
                              onClick={() => viewVersions(doc)}
                              className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#D42B92]/10 text-[#FF8EC5] border border-[#D42B92]/20 hover:bg-[#D42B92]/20 flex items-center gap-1"
                              title="Inspect version history"
                            >
                              <History className="w-2.5 h-2.5" />
                              v{doc.version}
                            </button>
                          )}
                        </div>

                        <p className="text-xs text-white/50 mt-1 line-clamp-1">{doc.description}</p>

                        <div className="flex items-center gap-3 text-[11px] text-white/40 mt-1.5">
                          <span className="capitalize">{doc.category}</span>
                          {doc.originalName && (
                            <>
                              <span>•</span>
                              <span className="truncate max-w-[200px]">{doc.originalName}</span>
                            </>
                          )}
                          {doc.updatedAt && (
                            <>
                              <span>•</span>
                              <span>Updated {new Date(doc.updatedAt).toLocaleDateString()}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Status Pill & Action Buttons */}
                    <div className="flex items-center justify-between lg:justify-end gap-3 shrink-0 pt-2 lg:pt-0 border-t border-[#2C2C34] lg:border-t-0">
                      <div>
                        {isApproved && (
                          <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Approved
                          </span>
                        )}
                        {isInReview && (
                          <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-500/15 text-amber-300 border border-amber-500/20 flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            In Review
                          </span>
                        )}
                        {isRejected && (
                          <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-rose-500/15 text-rose-300 border border-rose-500/20 flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Revision Needed
                          </span>
                        )}
                        {isNotUploaded && (
                          <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-[#08080A] text-[#8E8E99] border border-[#2C2C34]">
                            Not Uploaded
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Download button */}
                        {doc.fileUrl && (
                          <a
                            href={resolveFileUrl(doc.fileUrl)}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1.5 rounded-lg border border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4] hover:border-[#D42B92]/40 text-xs font-medium flex items-center gap-1.5 transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" />
                            Download
                          </a>
                        )}

                        {/* Upload / Resubmit Button */}
                        {isNotUploaded ? (
                          <button
                            onClick={() => openUploadModal(doc)}
                            className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#D42B92] to-[#b02279] hover:from-[#e0349f] hover:to-[#be2783] text-white text-xs font-medium flex items-center gap-1.5 transition-all shadow-sm shadow-[#D42B92]/20"
                          >
                            <Upload className="w-3.5 h-3.5" />
                            Upload
                          </button>
                        ) : isRejected ? (
                          <button
                            onClick={() => openUploadModal(doc)}
                            className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-medium flex items-center gap-1.5 transition-colors"
                          >
                            <Upload className="w-3.5 h-3.5" />
                            Resubmit
                          </button>
                        ) : (
                          <button
                            onClick={() => openUploadModal(doc)}
                            className="px-2.5 py-1.5 rounded-lg border border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4] text-xs font-medium flex items-center gap-1 transition-colors"
                            title="Upload newer version"
                          >
                            <Upload className="w-3.5 h-3.5" />
                            New Ver
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Rejection Feedback Callout (Visible inline) */}
                  {isRejected && doc.rejectionReason && (
                    <div className="mt-3 ml-13 p-3 rounded-xl border border-rose-500/20 bg-rose-500/5 text-xs text-rose-300 flex items-start gap-2.5">
                      <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-rose-200">Reviewer Feedback: </span>
                        {doc.rejectionReason}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload & Resubmission Modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="w-full max-w-lg rounded-2xl border border-[#2C2C34] bg-[#131318] p-6 shadow-2xl space-y-5 text-[#F2F2F4]">
            <div className="flex items-center justify-between border-b border-[#2C2C34] pb-4">
              <div>
                <h3 className="text-lg font-bold">
                  {targetItem && targetItem.status === 'Rejected'
                    ? 'Resubmit Document Revision'
                    : targetItem && targetItem.version > 0
                    ? `Upload New Version (v${(targetItem.version || 1) + 1})`
                    : 'Upload Document'}
                </h3>
                <p className="text-xs text-[#8E8E99] mt-0.5">
                  PDF, JPG, or PNG (Max 10 MB). Stored securely with mentor zero-trust ACL.
                </p>
              </div>
              <button
                onClick={() => setUploadModalOpen(false)}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {uploadError && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{uploadError}</span>
              </div>
            )}

            <form onSubmit={submitUpload} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-[#8E8E99] block mb-1">Document Title *</label>
                <input
                  type="text"
                  value={uploadHeading}
                  onChange={(e) => setUploadHeading(e.target.value)}
                  placeholder="e.g. Series Seed Pitch Deck"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-sm text-[#F2F2F4] placeholder-[#8E8E99]/50 focus:outline-none focus:border-[#D42B92]"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-medium text-[#8E8E99] block mb-1">Category</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-sm text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                >
                  <option value="Legal & Compliance">Legal & Compliance</option>
                  <option value="Fundraising">Fundraising</option>
                  <option value="Finance">Finance</option>
                  <option value="Compliance">Compliance</option>
                  <option value="IP & Legal">IP & Legal</option>
                  <option value="General">General Deliverable</option>
                </select>
              </div>

              {/* Drag & Drop File Area */}
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                  uploadFile
                    ? 'border-emerald-500/50 bg-emerald-500/[0.03]'
                    : 'border-[#2C2C34] hover:border-[#D42B92]/60 hover:bg-[#181820]'
                }`}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileInputChange}
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                />

                {uploadFile ? (
                  <div className="space-y-1">
                    <FileCheck className="w-8 h-8 text-emerald-400 mx-auto" />
                    <div className="text-sm font-semibold text-[#F2F2F4]">{uploadFile.name}</div>
                    <div className="text-xs text-[#8E8E99]">
                      {(uploadFile.size / (1024 * 1024)).toFixed(2)} MB • Ready to upload
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-8 h-8 text-[#8E8E99] mx-auto" />
                    <div className="text-sm font-medium text-[#F2F2F4]">
                      Drag and drop your file here, or <span className="text-[#D42B92] underline">browse</span>
                    </div>
                    <div className="text-[11px] text-[#8E8E99]">Accepted formats: PDF, JPG, PNG (Max 10MB)</div>
                  </div>
                )}
              </div>

              {/* Progress bar */}
              {uploading && (
                <div className="space-y-1">
                  <div className="h-1.5 w-full bg-[#2C2C34] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#D42B92] to-[#b02279] transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <div className="text-right text-[11px] text-[#8E8E99]">Uploading & verifying file...</div>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setUploadModalOpen(false)}
                  disabled={uploading}
                  className="px-4 py-2 rounded-xl border border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4] text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !uploadFile}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-[#D42B92] to-[#b02279] hover:from-[#e0349f] hover:to-[#be2783] disabled:opacity-50 text-white font-medium text-sm transition-all shadow-md shadow-[#D42B92]/20"
                >
                  {uploading ? 'Uploading...' : 'Upload & Queue Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Version History Drawer / Modal */}
      {versionModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-[#2C2C34] bg-[#131318] p-6 shadow-2xl space-y-4 text-[#F2F2F4]">
            <div className="flex items-center justify-between border-b border-[#2C2C34] pb-3">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-[#D42B92]" />
                <h3 className="text-base font-bold">Version History</h3>
              </div>
              <button
                onClick={() => setVersionModalOpen(false)}
                className="p-1 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingVersions ? (
              <div className="p-8 text-center text-[#8E8E99] text-sm">Loading version history...</div>
            ) : versionHistoryData?.versions?.length ? (
              <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                {versionHistoryData.versions.map((ver, idx) => (
                  <div
                    key={idx}
                    className="p-3 rounded-xl border border-[#2C2C34] bg-[#08080A] flex items-center justify-between gap-3 text-xs"
                  >
                    <div>
                      <div className="font-semibold text-[#F2F2F4] flex items-center gap-2">
                        <span>Version {ver.version || idx + 1}</span>
                        <span
                          className={`text-[10px] px-1.5 py-0.2 rounded ${
                            ver.status === 'Approved'
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                              : ver.status === 'Rejected'
                              ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                              : 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                          }`}
                        >
                          {ver.status || 'Uploaded'}
                        </span>
                      </div>
                      <div className="text-[#8E8E99] mt-1 truncate max-w-[200px]">
                        {ver.originalName || 'Document file'}
                      </div>
                      <div className="text-[10px] text-[#8E8E99]/60 mt-0.5">
                        {ver.uploadedAt ? new Date(ver.uploadedAt).toLocaleString() : 'Past revision'}
                      </div>
                    </div>

                    {ver.fileUrl && (
                      <a
                        href={resolveFileUrl(ver.fileUrl)}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2 rounded-lg border border-[#2C2C34] hover:border-[#D42B92]/40 text-[#8E8E99] hover:text-[#F2F2F4]"
                        title="Download this version"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-[#8E8E99] text-xs">No prior versions recorded.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
